import React, { Component } from "react";
import * as Cookies from "es-cookie";
import { Row, Col } from "react-bootstrap";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import * as compose from "lodash.flowright";
import { withRouter } from "react-router-dom";
import SelectDropDown from "../../../../neetjee_guru/components/selectdropdown/SelectDropDown";
import TabSection from "./TabSection";
import { PageShimmer } from "./Shimmer";
import { examStreamsVar, examKeysVar, examStreamsKey } from './examStreams';

// Weak-chapter block for the planner query. Includes
// the source exams (Part B) and revision tracking (Part C/D).
const CHAPTER_FIELDS = `
  chapter
  chapter_id
  accuracy
  attempted
  priority
  sources { exam_paper_id exam_name accuracy attempted }
  revisionStatus
  accuracyBefore
  accuracyAfter
`;

const GET_STUDENT_PERFORMANCE_REPORT = gql`
  query (
    $student_user_exam_id: Int!
    $exam_type: Int
    $exam_streams: [Int], $exam_keys: [String]
    $exampaper_id: Int
  ) {
    getStudentPerformanceReport(
      student_user_exam_id: $student_user_exam_id
      exam_type: $exam_type
      exam_streams: $exam_streams, exam_keys: $exam_keys
    ) {
      # Exam list to populate the paper filter (id + name per attempted exam).
      examSubjectMatrix { exam_paper_id exam_name }
      revisionSchedule(exampaper_id: $exampaper_id) {
        day
        label
        subtitle
        date
        focus
        items {
          subject
          subject_id
          topic
          topic_id
          chapter
          chapter_id
          percentage
          priority
          revisit
          note
        }
      }
      revisionPlanner(exampaper_id: $exampaper_id) {
        subject
        subject_id
        weakChapterCount
        chapters { ${CHAPTER_FIELDS} }
      }
    }
  }
`;

const MARK_TOPIC_REVISED = gql`
  mutation (
    $student_user_exam_id: Int!
    $chapter_id: Int!
    $subject_id: Int
    $accuracy_before: Float
    $revised: Boolean
  ) {
    markTopicRevised(
      student_user_exam_id: $student_user_exam_id
      chapter_id: $chapter_id
      subject_id: $subject_id
      accuracy_before: $accuracy_before
      revised: $revised
    )
  }
`;

const num = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
};

// Priority → pill colors (High = most urgent).
const PRIORITY_META = {
    High: { bg: "#fef2f2", color: "#dc2626" },
    Medium: { bg: "#fffbeb", color: "#d97706" },
    Low: { bg: "#eff6ff", color: "#2563eb" },
};

// Rotating accent color per subject card.
const SUBJECT_COLORS = ["#6366f1", "#22c55e", "#f59e0b", "#ef4444", "#0ea5e9", "#a855f7", "#ec4899"];

class RevisionPlannerTab extends Component {
    constructor(props) {
        super(props);
        this.state = {
            revisionPlanner: [],
            revisionSchedule: [],
            examOptions: [{ value: 0, label: "All exams" }],
            selectedExam: { value: 0, label: "All exams" },
            loading: false,
            scheduleOpen: false, // 7-day schedule collapsed by default (board is primary)
            // chapter_id -> optimistic revision state so the UI reacts instantly to
            // Mark-revised before the refetch lands (and even if the table is absent).
            revisionOverrides: {},
            saving: {},
        };
    }

    componentDidMount() {
        this.getReport();
    }

    // Re-fetch when the header exam-stream (Mains/Advanced) changes.
    componentDidUpdate(prevProps) {
        if (examStreamsKey(prevProps.examStreams) !== examStreamsKey(this.props.examStreams)) {
            this.setState({ selectedExam: { value: 0, label: "All exams" } }, () => this.getReport());
        }
    }

    getReport = async () => {
        this.setState({ loading: true });
        try {
            const examPaperId = Number(this.state.selectedExam?.value) || 0;
            const result = await this.props.client.query({
                query: GET_STUDENT_PERFORMANCE_REPORT,
                variables: {
                    student_user_exam_id: parseInt(Cookies.get("student_user_exam_id")),
                    exam_type: 0,
                    exam_streams: examStreamsVar(this.props.examStreams),
                    exam_keys: examKeysVar(this.props.examKeys),
                    exampaper_id: examPaperId || null,
                },
                fetchPolicy: "cache-first",
            });
            const data = result?.data?.getStudentPerformanceReport;
            // Build the exam-paper filter options from the attempted exams.
            const matrix = data?.examSubjectMatrix || [];
            const seen = new Set();
            const examOptions = [{ value: 0, label: "All exams" }];
            matrix.forEach((m) => {
                if (m.exam_paper_id && !seen.has(m.exam_paper_id)) {
                    seen.add(m.exam_paper_id);
                    examOptions.push({ value: m.exam_paper_id, label: m.exam_name || `Exam ${m.exam_paper_id}` });
                }
            });
            this.setState((prev) => ({
                revisionPlanner: data?.revisionPlanner || [],
                revisionSchedule: data?.revisionSchedule || [],
                examOptions: examOptions.length > 1 ? examOptions : prev.examOptions,
                loading: false,
                revisionOverrides: {}, // server is now source of truth
            }));
        } catch (error) {
            console.log(error);
            this.setState({ revisionPlanner: [], revisionSchedule: [], loading: false });
        }
    };

    // SelectDropDown calls handleChange(name, value, selectedOption).
    handleExamChange = (_name, _value, selected) => {
        this.setState({ selectedExam: selected || { value: 0, label: "All exams" } }, () => this.getReport());
    };

    // Mark / un-mark a weak chapter as revised. Updates the UI optimistically, then
    // persists via the mutation (no-op server-side if the tracking table is absent).
    toggleRevised = async (chapter, subjectId) => {
        const cid = chapter.chapter_id;
        if (cid == null) return; // composite chapters have no single id to track
        const current = this.effectiveStatus(chapter);
        const nextRevised = current === "pending";
        this.setState((prev) => ({
            revisionOverrides: {
                ...prev.revisionOverrides,
                [cid]: nextRevised ? { revisionStatus: "revised", accuracyBefore: chapter.accuracy } : { revisionStatus: "pending" },
            },
            saving: { ...prev.saving, [cid]: true },
        }));
        try {
            await this.props.client.mutate({
                mutation: MARK_TOPIC_REVISED,
                variables: {
                    student_user_exam_id: parseInt(Cookies.get("student_user_exam_id")),
                    chapter_id: cid,
                    subject_id: subjectId || 0,
                    accuracy_before: chapter.accuracy != null ? Number(chapter.accuracy) : null,
                    revised: nextRevised,
                },
            });
        } catch (e) {
            console.log(e);
        } finally {
            this.setState((prev) => ({ saving: { ...prev.saving, [cid]: false } }));
        }
    };

    // Effective revision status = optimistic override if present, else server value.
    effectiveStatus = (chapter) => {
        const o = this.state.revisionOverrides[chapter.chapter_id];
        return (o && o.revisionStatus) || chapter.revisionStatus || "pending";
    };

    // Lower accuracy = weaker = higher priority → warmer color.
    accuracyColor = (pct) => {
        if (pct < 60) return "#ef4444"; // red
        if (pct < 80) return "#f59e0b"; // amber
        return "#16a34a"; // green
    };

    // Top banner — explains the plan is auto-generated from weak topics.
    renderBanner = (name, totalWeak, examLabel) => (
        <div
            className="d-flex align-items-center mb-4 p-3"
            style={{
                gap: "12px",
                borderRadius: "14px",
                background: "linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%)",
                border: "1px solid #e0e7ff",
            }}
        >
            <div
                className="d-inline-flex align-items-center justify-content-center text-white flex-shrink-0"
                style={{
                    width: "34px",
                    height: "34px",
                    borderRadius: "10px",
                    background: "linear-gradient(135deg, #ec4899 0%, #d946ef 100%)",
                }}
            >
                <i className="fas fa-lightbulb" style={{ fontSize: "15px" }} />
            </div>
            <span className="text-dark" style={{ fontSize: "14px" }}>
                Auto-generated from <strong>{name}'s</strong> weakest topics
                {examLabel ? <> in <strong>{examLabel}</strong></> : null}
                {totalWeak ? <> — <strong>{totalWeak}</strong> weak chapters</> : null} — these give the biggest score gains.
            </span>
        </div>
    );

    // Flatten every weak chapter (across subjects) into one pool, tagging each with
    // its subject name + accent, then bucket by effective status for the Kanban.
    buildKanban = (planner) => {
        const pool = [];
        (planner || []).forEach((subj, si) => {
            (subj.chapters || []).forEach((c) => {
                pool.push({
                    ...c,
                    subject: subj.subject,
                    subject_id: subj.subject_id,
                    accent: SUBJECT_COLORS[si % SUBJECT_COLORS.length],
                });
            });
        });
        const cols = { pending: [], revised: [], retested: [] };
        pool.forEach((c) => {
            const st = this.effectiveStatus(c);
            (cols[st] || cols.pending).push(c);
        });
        // weakest first in "To revise"; keep natural order elsewhere
        cols.pending.sort((a, b) => num(a.accuracy) - num(b.accuracy));
        return cols;
    };

    // A compact Kanban card for one weak chapter.
    renderKanbanCard = (c, column) => {
        const acc = num(c.accuracy);
        const color = this.accuracyColor(acc);
        const pr = PRIORITY_META[c.priority] || { bg: "#f1f5f9", color: "#64748b" };
        const trackable = c.chapter_id != null;
        const saving = this.state.saving[c.chapter_id];
        const sources = c.sources || [];
        const improved =
            column === "retested" && c.accuracyBefore != null && c.accuracyAfter != null
                ? num(c.accuracyAfter) - num(c.accuracyBefore)
                : null;
        const railColor = column === "retested" ? "#16a34a" : column === "revised" ? "#6366f1" : color;

        return (
            <div className="sp-kb-card" key={c.chapter_id ?? c.chapter} style={{ borderLeftColor: railColor }}>
                {/* title row */}
                <div className="d-flex align-items-start justify-content-between" style={{ gap: 6 }}>
                    <div className="d-flex align-items-center" style={{ gap: 6, minWidth: 0 }}>
                        <span style={{ width: 7, height: 7, borderRadius: "50%", background: c.accent, flex: "none" }} />
                        <span className="font-weight-bold text-dark text-truncate" style={{ fontSize: 12.5 }} title={c.chapter}>
                            {c.chapter || "-"}
                        </span>
                    </div>
                    <span className="sp-status-pill flex-shrink-0" style={{ background: pr.bg, color: pr.color, fontSize: 9.5 }}>
                        {c.priority || "-"}
                    </span>
                </div>
                <div className="text-muted" style={{ fontSize: 10.5, marginTop: 1 }}>{c.subject}</div>

                {/* accuracy bar (or before→after for re-tested) */}
                {improved != null ? (
                    <div className="d-flex align-items-center mt-2" style={{ gap: 6, fontSize: 11.5, fontWeight: 700, color: "#15803d" }}>
                        <span style={{ color: "#94a3b8" }}>{num(c.accuracyBefore)}%</span>
                        <i className="fas fa-arrow-right" style={{ fontSize: 9, color: "#cbd5e1" }} />
                        <span>{num(c.accuracyAfter)}%</span>
                        <span style={{ marginLeft: "auto", fontSize: 10.5 }}>
                            <i className="fas fa-arrow-up mr-1" style={{ fontSize: 8 }} />{improved >= 0 ? "+" : ""}{improved.toFixed(0)} pts
                        </span>
                    </div>
                ) : (
                    <div className="d-flex align-items-center mt-2" style={{ gap: 7 }}>
                        <div className="sp-kb-track">
                            <div className="sp-kb-fill" style={{ width: `${Math.min(Math.max(acc, 0), 100)}%`, background: color }} />
                        </div>
                        <span className="font-weight-bold" style={{ fontSize: 10.5, color, minWidth: 30, textAlign: "right" }}>{acc}%</span>
                    </div>
                )}

                {/* source exams (compact) */}
                {sources.length > 0 ? (
                    <div className="d-flex flex-wrap align-items-center mt-2" style={{ gap: 4 }}>
                        {sources.slice(0, 2).map((s, i) => (
                            <span key={s.exam_paper_id ?? i} className="sp-kb-chip" title={`${num(s.accuracy)}% in ${s.exam_name}`}>
                                {s.exam_name} · <b style={{ color: this.accuracyColor(num(s.accuracy)) }}>{num(s.accuracy)}%</b>
                            </span>
                        ))}
                        {sources.length > 2 ? (
                            <span style={{ fontSize: 10, color: "#94a3b8" }}>+{sources.length - 2}</span>
                        ) : null}
                    </div>
                ) : null}

                {/* action: move across the board */}
                {trackable ? (
                    <button
                        className={`sp-kb-btn mt-2 ${column === "pending" ? "is-primary" : ""}`}
                        onClick={() => this.toggleRevised(c, c.subject_id)}
                        disabled={saving}
                    >
                        {saving ? (
                            <><i className="fas fa-spinner fa-spin mr-1" style={{ fontSize: 9 }} />Saving…</>
                        ) : column === "pending" ? (
                            <><i className="fas fa-check mr-1" style={{ fontSize: 9 }} />Mark revised</>
                        ) : (
                            <><i className="fas fa-undo mr-1" style={{ fontSize: 9 }} />To revise</>
                        )}
                    </button>
                ) : null}
            </div>
        );
    };

    // One Kanban column.
    renderKanbanColumn = (key, title, dot, cards) => (
        <div className="sp-kb-col">
            <div className="sp-kb-col-head">
                <span className="sp-kb-dot" style={{ background: dot }} />
                <span className="sp-kb-title" style={{ color: dot }}>{title}</span>
                <span className="sp-kb-count">{cards.length}</span>
            </div>
            {cards.length > 0 ? (
                <div className="sp-kb-list">{cards.map((c) => this.renderKanbanCard(c, key))}</div>
            ) : (
                <div className="sp-kb-empty">
                    {key === "pending" ? "All caught up 🎉" : key === "revised" ? "Nothing marked yet" : "No re-tests yet"}
                </div>
            )}
        </div>
    );

    // Kanban board — To Revise / Revised / Re-tested with a progress summary.
    renderPlanner = (planner) => {
        const cols = this.buildKanban(planner);
        const total = cols.pending.length + cols.revised.length + cols.retested.length;
        const done = cols.revised.length + cols.retested.length;
        const pct = total ? Math.round((done / total) * 100) : 0;

        if (total === 0) {
            return (
                <div className="sp-af-card text-center text-muted py-4 mb-4" style={{ fontSize: 14 }}>No weak topics found.</div>
            );
        }
        return (
            <div className="mb-4">
                <div className="d-flex align-items-center justify-content-between flex-wrap mb-2" style={{ gap: 10 }}>
                    <h6 className="font-weight-bold text-dark mb-0" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        <i className="fas fa-tasks mr-2" style={{ color: "#6366f1" }} />Revision Board
                    </h6>
                    <div className="d-flex align-items-center" style={{ gap: 10, minWidth: 200, flex: "1 1 220px", maxWidth: 340 }}>
                        <div className="sp-kb-track" style={{ height: 7 }}>
                            <div className="sp-kb-fill" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#6366f1,#16a34a)" }} />
                        </div>
                        <span className="font-weight-bold text-muted flex-shrink-0" style={{ fontSize: 12 }}>{done}/{total} done</span>
                    </div>
                </div>
                <div className="sp-kanban">
                    {this.renderKanbanColumn("pending", "To Revise", "#ef4444", cols.pending)}
                    {this.renderKanbanColumn("revised", "Revised", "#6366f1", cols.revised)}
                    {this.renderKanbanColumn("retested", "Re-tested", "#16a34a", cols.retested)}
                </div>
            </div>
        );
    };

    // One topic as a compact inline pill inside a day row. Color-coded by current
    // accuracy; a small ↻ marks a spaced-revisit slot.
    renderScheduleItem = (item, i) => {
        const pct = Math.min(Math.max(num(item.percentage), 0), 100);
        const color = this.accuracyColor(pct);
        const revisit = item.revisit === true;
        const label = item.chapter || item.topic || "-";
        return (
            <span
                className="sp-sched-pill"
                key={`${item.chapter_id ?? item.chapter}-${item.topic_id ?? i}-${revisit ? "r" : "f"}`}
                title={`${item.subject ? item.subject + " · " : ""}${label}${item.topic && item.topic !== label ? " · " + item.topic : ""} — ${pct}% accuracy`}
                style={{ borderLeft: `3px solid ${color}` }}
            >
                {revisit ? <i className="fas fa-redo mr-1" style={{ fontSize: 8, color: "#6366f1" }} /> : null}
                <span className="sp-sched-pill-name">{label}</span>
                <b style={{ color, marginLeft: 5 }}>{pct}%</b>
            </span>
        );
    };

    // A single day as a tight row: compact day chip + date/focus + inline topic pills.
    renderScheduleDay = (day, idx) => {
        const items = day.items || [];
        const isRevisit = (day.day || 0) > 4;
        return (
            <div className="sp-sched-row" key={day.day ?? idx}>
                <div className="sp-sched-daychip" style={{ background: isRevisit ? "#ede9fe" : "#eef2ff", color: isRevisit ? "#6d28d9" : "#4338ca" }}>
                    <span style={{ fontSize: 9, opacity: 0.75, letterSpacing: "0.5px" }}>DAY</span>
                    <span style={{ fontSize: 17, fontWeight: 800, lineHeight: 1 }}>{day.day}</span>
                </div>
                <div className="sp-sched-body">
                    <div className="d-flex align-items-center flex-wrap" style={{ gap: 8, marginBottom: 6 }}>
                        <span className="font-weight-bold text-dark" style={{ fontSize: 12.5 }}>{day.subtitle || day.label}</span>
                        {day.date ? <span className="text-muted" style={{ fontSize: 11 }}><i className="far fa-calendar mr-1" style={{ fontSize: 9 }} />{day.date}</span> : null}
                        {day.focus ? <span className="sp-sched-focus"><i className="fas fa-bullseye mr-1" style={{ fontSize: 9 }} />{day.focus}</span> : null}
                    </div>
                    {items.length > 0 ? (
                        <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                            {items.map((it, i) => this.renderScheduleItem(it, i))}
                        </div>
                    ) : (
                        <div className="text-muted" style={{ fontSize: 12 }}>No topics.</div>
                    )}
                </div>
            </div>
        );
    };

    renderSchedule = (schedule) => (
        <div className="mb-4 mt-3">
            <div className="text-muted mb-3" style={{ fontSize: 12, lineHeight: 1.4 }}>
                Weakest topics first (Days 1&ndash;4), then the hardest ones return for a
                <span style={{ color: "#4f46e5", fontWeight: 600 }}> spaced revisit</span> (Days 5&ndash;7).
                Each pill is a topic, colored by your <strong>current accuracy</strong>.
            </div>
            {schedule.length > 0 ? (
                <div className="sp-sched-list">{schedule.map((d, i) => this.renderScheduleDay(d, i))}</div>
            ) : (
                <div className="sp-kb-empty">No revision schedule generated yet.</div>
            )}
        </div>
    );

    // Collapsible wrapper around the 7-day schedule (board is the primary view).
    renderScheduleSection = (schedule) => {
        const { scheduleOpen } = this.state;
        const count = (schedule || []).length;
        return (
            <div className="mt-4">
                <button
                    onClick={() => this.setState((p) => ({ scheduleOpen: !p.scheduleOpen }))}
                    className="d-flex align-items-center w-100"
                    style={{
                        gap: 10, background: "#f8fafc", border: "1px solid #eef1f6",
                        borderRadius: 12, padding: "12px 16px", cursor: "pointer", textAlign: "left",
                    }}
                >
                    <i className="far fa-calendar-alt" style={{ color: "#6d28d9", fontSize: 15 }} />
                    <span className="font-weight-bold text-dark" style={{ fontSize: 14 }}>7-Day Revision Schedule</span>
                    {count ? (
                        <span className="sp-kb-count" style={{ marginLeft: 4 }}>{count} days</span>
                    ) : null}
                    <i className={`fas fa-chevron-${scheduleOpen ? "up" : "down"} ml-auto`} style={{ color: "#94a3b8", fontSize: 13 }} />
                </button>
                {scheduleOpen ? this.renderSchedule(schedule) : null}
            </div>
        );
    };

    // Exam-paper filter — scopes the whole plan to one exam (or all exams).
    renderFilterBar = () => {
        const { examOptions, selectedExam } = this.state;
        if (!examOptions || examOptions.length <= 1) return null;
        return (
            <div className="d-flex flex-wrap align-items-center justify-content-end mb-3" style={{ gap: 8 }}>
                <span className="font-weight-bold text-muted" style={{ fontSize: 13 }}>Based on exam</span>
                <div style={{ minWidth: 200 }}>
                    <SelectDropDown
                        stateData={selectedExam}
                        handleChange={this.handleExamChange}
                        name="selectedExam"
                        options={examOptions}
                        placeholderName="Exam"
                    />
                </div>
            </div>
        );
    };

    render() {
        const { revisionPlanner, revisionSchedule, loading, selectedExam } = this.state;
        const name = this.props.student?.student_name || "this student";
        const totalWeak = revisionPlanner.reduce(
            (sum, s) => sum + (s.weakChapterCount != null ? num(s.weakChapterCount) : (s.chapters || []).length),
            0
        );
        const examLabel = selectedExam && selectedExam.value ? selectedExam.label : null;

        return (
            <TabSection
                icon="fa-calendar-check"
                title="Revision Planner"
                desc="A personalised day-wise revision schedule based on weak areas."
            >
                {this.renderFilterBar()}
                {loading ? (
                    <PageShimmer />
                ) : (
                    <>
                        {this.renderBanner(name, totalWeak, examLabel)}
                        {this.renderPlanner(revisionPlanner)}
                        {this.renderScheduleSection(revisionSchedule)}
                    </>
                )}
            </TabSection>
        );
    }
}

export default compose(withApollo, withRouter)(RevisionPlannerTab);
